import React, { useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import {
  getRepairRequestProcessProgress,
  getRepairRequestProcessStepLabel,
  getRepairRequestProcessSteps,
} from '../../utils/repairRequestWorkflow';
import { RepairStatus } from '../../types/firestore';

interface RepairRequestProcessStepperProps {
  status: RepairStatus;
  compactOnMobile?: boolean;
  defaultExpanded?: boolean;
}

const RepairRequestProcessStepper: React.FC<RepairRequestProcessStepperProps> = ({
  status,
  compactOnMobile = false,
  defaultExpanded = false,
}) => {
  const steps = getRepairRequestProcessSteps();
  const { currentStepIndex, completedStepCount, isTerminal } =
    getRepairRequestProcessProgress(status);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const summaryStep = steps[Math.min(currentStepIndex, steps.length - 1)];
  const currentStepLabel = getRepairRequestProcessStepLabel(status);

  const details = (
    <div className="min-w-0 space-y-3 overflow-hidden">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {steps.map((step, index) => {
          const isCompleted = index < completedStepCount;
          const isCurrent = !isCompleted && index === currentStepIndex;
          const connectorCompleted = index < completedStepCount;
          return (
            <div
              key={step.id}
              className="flex min-w-[2rem] flex-1 items-center last:flex-[0_0_auto]"
            >
              <div
                className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs font-semibold ${
                  isCompleted
                    ? 'bg-ratuj-300 border-ratuj-300 text-primary-dark-100'
                    : isCurrent
                      ? 'bg-accent-light-300 border-accent-light-300 text-primary-dark-100'
                      : 'bg-primary-dark-200 border-primary-dark-300 text-primary-light-400'
                }`}
              >
                {isCompleted ? <CheckIcon className="w-4 h-4" /> : index + 1}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`mx-1.5 h-[2px] min-w-[0.75rem] flex-1 ${
                    connectorCompleted ? 'bg-ratuj-300' : 'bg-primary-dark-300'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="grid min-w-0 grid-cols-2 gap-2 md:grid-cols-5 md:gap-3">
        {steps.map((step, index) => {
          const isCurrent =
            index === currentStepIndex || (isTerminal && index === steps.length - 1);
          return (
            <div key={`${step.id}-label`} className="min-w-0">
              <p
                className={`text-xs font-medium ${
                  isCurrent ? 'text-primary-light-100' : 'text-primary-light-400'
                }`}
              >
                {isCurrent ? currentStepLabel : step.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (!compactOnMobile) {
    return details;
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-primary-dark-200/16 px-3 py-2 ring-1 ring-primary-dark-300/14 md:hidden">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 break-words text-sm text-primary-light-100">
            {`Krok ${currentStepIndex + 1}/${steps.length}`}
            <span className="text-primary-light-400">
              {' '}
              • {currentStepLabel || summaryStep?.label || 'Etap'}
            </span>
          </p>
          <button
            type="button"
            className="text-xs font-medium text-ratuj-300 hover:text-ratuj-200 focus-outline rounded"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? 'Ukryj etapy' : 'Pokaż etapy'}
          </button>
        </div>
      </div>

      <div className={`${isExpanded ? 'block' : 'hidden'} md:block`}>{details}</div>
    </div>
  );
};

export default RepairRequestProcessStepper;
